// Cell-grid + meetvlak-detectie voor de jaaranalyse (zie §3.3 / §3.4).
//
// Een meetvlak is een horizontaal vlak waarop we de schaduw-statistieken
// uitrekenen. Auto-detectie regels conform §3.3:
//   1. Blokken van type tuin of bestrating → meetvlak op z = 0 (grondniveau)
//   2. Blokken van type gebouw met heightTop > 0 → meetvlak op z = heightTop
//      (het dakvlak; meestal is daar een potentieel zonnepaneel-installatie).
//   3. Blokken van type gebouw met heightTop = 0 → meetvlak op z = 0
//      (een plat gebouw werpt geen schaduw maar functioneert wel als een
//      ontvangend grondvlak — vergelijkbaar met bestrating).
//   4. Geen losse meetvlakken in v1.
//
// Het meetvlak gebruikt de polygoon van het bron-blok als 2D contour, met
// een 0,5 m × 0,5 m grid (default; kan in de UI gehalveerd voor extra
// precisie, zie §8.1).

import type { Block } from "../types/scenario";
import { pointInPolygon } from "../utils/geo";

const EARTH_R = 6_378_137;

export interface MeasurementSurface {
  /** Stabiele identificatie: blok-id + suffix om grond/dak te onderscheiden. */
  id: string;
  blockId: string;
  surfaceKind: "ground" | "roof";
  /** Hoogte van het meetvlak in meters boven maaiveld. */
  z: number;
  /** Polygoon-rand van het meetvlak (in [lng, lat]). */
  polygon: [number, number][];
  label: string;
}

export interface GridCell {
  /** Centerpunt in [lng, lat]. */
  lngLat: [number, number];
  /** Lokale meters t.o.v. de projectie van het scenario. */
  x: number;
  y: number;
}

export interface CellGrid {
  surface: MeasurementSurface;
  /** Resolutie in meters (bv. 0.5). */
  resolution: number;
  cells: GridCell[];
}

interface LocalProj {
  refLat: number;
  refLng: number;
  mPerLat: number;
  mPerLng: number;
}

export function makeProjection(refLat: number, refLng: number): LocalProj {
  const mPerLat = (Math.PI * EARTH_R) / 180;
  const mPerLng = mPerLat * Math.cos((refLat * Math.PI) / 180);
  return { refLat, refLng, mPerLat, mPerLng };
}

/**
 * Detecteer welke meetvlakken een scenario bevat. Volgorde is stabiel:
 * eerst alle grondvlakken (op block-volgorde), daarna alle dakvlakken.
 */
export function detectMeasurementSurfaces(blocks: Block[]): MeasurementSurface[] {
  const surfaces: MeasurementSurface[] = [];
  for (const b of blocks) {
    const isGroundBlock =
      b.type === "tuin" ||
      b.type === "bestrating" ||
      (b.type === "gebouw" && b.heightTop === 0);
    if (isGroundBlock) {
      surfaces.push({
        id: `${b.id}:ground`,
        blockId: b.id,
        surfaceKind: "ground",
        z: 0,
        polygon: b.polygon,
        label: `${b.name} (grond)`,
      });
    }
  }
  for (const b of blocks) {
    if (b.type === "gebouw" && b.heightTop > 0) {
      surfaces.push({
        id: `${b.id}:roof`,
        blockId: b.id,
        surfaceKind: "roof",
        z: b.heightTop,
        polygon: b.polygon,
        label: `${b.name} (dak)`,
      });
    }
  }
  return surfaces;
}

/**
 * Bouw een grid van celcentra binnen de polygoon van een meetvlak.
 *
 * `resolution` is in meters; default 0,5 (per §3.3). De projectie
 * (`refLat`, `refLng`) wordt centraal gekozen zodat de equirectangular-
 * fout binnen het scenario verwaarloosbaar blijft.
 */
export function buildCellGrid(
  surface: MeasurementSurface,
  proj: LocalProj,
  resolution: number,
): CellGrid {
  // Bereken bbox in lokale meters.
  const xs: number[] = [];
  const ys: number[] = [];
  const localPolygon: [number, number][] = surface.polygon.map((p) => {
    const x = (p[0] - proj.refLng) * proj.mPerLng;
    const y = (p[1] - proj.refLat) * proj.mPerLat;
    xs.push(x);
    ys.push(y);
    return [x, y];
  });
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const cells: GridCell[] = [];
  // Plaats celcentra op een raster met halve-cel offset, beginnend zodat
  // de cellen netjes binnen de bbox vallen.
  for (let y = minY + resolution / 2; y <= maxY; y += resolution) {
    for (let x = minX + resolution / 2; x <= maxX; x += resolution) {
      if (pointInLocalPolygon([x, y], localPolygon)) {
        const lng = proj.refLng + x / proj.mPerLng;
        const lat = proj.refLat + y / proj.mPerLat;
        cells.push({ lngLat: [lng, lat], x, y });
      }
    }
  }
  return { surface, resolution, cells };
}

/** Versnelde point-in-polygon voor lokale-meter coördinaten. */
function pointInLocalPolygon(p: [number, number], polygon: [number, number][]): boolean {
  // Hergebruik dezelfde ray-casting als utils/geo.ts maar in meters.
  return pointInPolygon(p, polygon);
}
